import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';

const TaxBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $impact }) =>
    $impact === 'increases' ? '#d32f2f15' :
    $impact === 'exempt' ? '#2e7d3215' :
    '#9e9e9e15'};
  color: ${({ $impact }) =>
    $impact === 'increases' ? '#d32f2f' :
    $impact === 'exempt' ? '#2e7d32' :
    '#9e9e9e'};
`;

const useBenefitApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [categories, setCategories] = useState([]);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    api.get('/v1/benefit-categories?pageSize=1000').then((res) => {
      setCategories(res.data?.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/benefits?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/benefits', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/benefits/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/benefits/${ids[0]}`);
      } else {
        await api.post('/v1/benefits/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const periodicityOptions = useMemo(() => [
    { id: 'monthly', name: t('benefit.periodicityMonthly') },
    { id: 'yearly', name: t('benefit.periodicityYearly') },
    { id: 'one-time', name: t('benefit.periodicityOneTime') },
    { id: 'on-demand', name: t('benefit.periodicityOnDemand') },
    { id: 'continuous', name: t('benefit.periodicityContinuous') },
  ], [t]);

  const taxImpactOptions = useMemo(() => [
    { id: 'none', name: t('benefit.taxImpactNone') },
    { id: 'exempt', name: t('benefit.taxImpactExempt') },
    { id: 'increases', name: t('benefit.taxImpactIncreases') },
  ], [t]);

  const taxImpactLabels = useMemo(() => ({
    none: t('benefit.taxImpactNone'),
    exempt: t('benefit.taxImpactExempt'),
    increases: t('benefit.taxImpactIncreases'),
  }), [t]);

  const periodicityLabels = useMemo(() => ({
    monthly: t('benefit.periodicityMonthly'),
    yearly: t('benefit.periodicityYearly'),
    'one-time': t('benefit.periodicityOneTime'),
    'on-demand': t('benefit.periodicityOnDemand'),
    continuous: t('benefit.periodicityContinuous'),
  }), [t]);

  const booleanOptions = useMemo(() => [
    { id: 'true', name: t('common.yes') },
    { id: 'false', name: t('common.no') },
  ], [t]);

  const categoryDropdownOptions = useMemo(
    () => categories.map((c) => ({ id: c.id, name: c.name })),
    [categories]
  );

  const labelOf = (list) => (val) => list.find((o) => o.id === val)?.name || val;

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('benefit.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'benefitCategoryId', label: t('benefit.categoryName'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: categoryDropdownOptions.map((o) => o.id),
          defaultOp: 'eq',
          renderValue: labelOf(categoryDropdownOptions),
        },
        customFilterListOptions: { render: labelOf(categoryDropdownOptions) },
        // Display the resolved category name from the response (categoryName)
        customBodyRenderLite: (dataIndex) => data[dataIndex]?.categoryName || '-',
      },
    },
    numericFilterColumn('monthlyCostCompany', t('benefit.monthlyCostCompany'), {
      decimals: 2,
      suffix: '₺',
      customBodyRender: (v) => `${Number(v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`,
    }),
    {
      name: 'periodicity', label: t('benefit.periodicity'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: periodicityOptions.map((o) => o.id),
          defaultOp: 'eq',
          renderValue: labelOf(periodicityOptions),
        },
        customFilterListOptions: { render: labelOf(periodicityOptions) },
        customBodyRender: (v) => periodicityLabels[v] || v,
      },
    },
    {
      name: 'taxImpact', label: t('benefit.taxImpact'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: taxImpactOptions.map((o) => o.id),
          defaultOp: 'eq',
          renderValue: labelOf(taxImpactOptions),
        },
        customFilterListOptions: { render: labelOf(taxImpactOptions) },
        customBodyRender: (v) => <TaxBadge $impact={v}>{taxImpactLabels[v] || v}</TaxBadge>,
      },
    },
    {
      name: 'isCafeteriaEligible', label: t('benefit.isCafeteriaEligible'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: booleanOptions.map((o) => o.id),
          defaultOp: 'eq',
          renderValue: labelOf(booleanOptions),
        },
        customFilterListOptions: { render: labelOf(booleanOptions) },
        customBodyRender: (v) => (v ? t('common.yes') : t('common.no')),
      },
    },
    {
      name: 'supportsDependents', label: t('benefit.supportsDependents'),
      options: {
        filter: false, sort: false, display: false,
        customBodyRender: (v) => (v ? t('common.yes') : t('common.no')),
      },
    },
    { name: 'provider', label: t('benefit.provider'), options: { filter: false, sort: false, display: false } },
  ], [t, periodicityLabels, taxImpactLabels, periodicityOptions, taxImpactOptions, booleanOptions, categoryDropdownOptions, data]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('benefit.name'), type: 'text', grid: 8, required: true },
    {
      name: 'benefitCategoryId', label: t('benefit.categoryName'), type: 'select', grid: 4, required: true,
      options: categories, idKey: 'id', labelKey: 'name',
    },
    { name: 'description', label: t('benefit.description'), type: 'textarea', grid: 12 },
    { name: 'monthlyCostCompany', label: t('benefit.monthlyCostCompany'), type: 'number', grid: 4, required: true },
    { name: 'monthlyCostEmployee', label: t('benefit.monthlyCostEmployee'), type: 'number', grid: 4 },
    {
      name: 'periodicity', label: t('benefit.periodicity'), type: 'select', grid: 4, required: true,
      options: periodicityOptions, idKey: 'id', labelKey: 'name',
    },
    { name: 'effectiveStartDate', label: t('benefit.effectiveStartDate'), type: 'date', grid: 6, required: true },
    { name: 'effectiveEndDate', label: t('benefit.effectiveEndDate'), type: 'date', grid: 6 },
    { name: 'supportsDependents', label: t('benefit.supportsDependents'), type: 'checkbox', grid: 4 },
    { name: 'costPerDependent', label: t('benefit.costPerDependent'), type: 'number', grid: 4 },
    { name: 'seniorityLockDays', label: t('benefit.seniorityLockDays'), type: 'number', grid: 4 },
    { name: 'isCafeteriaEligible', label: t('benefit.isCafeteriaEligible'), type: 'checkbox', grid: 4 },
    { name: 'cafeteriaPoints', label: t('benefit.cafeteriaPoints'), type: 'number', grid: 4 },
    { name: 'requiresSgkNotification', label: t('benefit.requiresSgkNotification'), type: 'checkbox', grid: 4 },
    {
      name: 'taxImpact', label: t('benefit.taxImpact'), type: 'select', grid: 6, required: true,
      options: taxImpactOptions, idKey: 'id', labelKey: 'name',
    },
    { name: 'provider', label: t('benefit.provider'), type: 'text', grid: 6 },
    { name: 'eligibilityRule', label: t('benefit.eligibilityRule'), type: 'textarea', grid: 12 },
  ], [t, categories, periodicityOptions, taxImpactOptions]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useBenefitApi;
